import {
  getGetFrontRichiesteIdPagamentiQueryKey,
  getGetFrontRichiesteIdPreventiviQueryKey,
  getGetFrontRichiesteIdQueryKey,
  PreventivoDTO,
  usePutFrontRichiesteIdPreventiviIdAccettazione,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { useTranslation } from '@framework/i18n';
import { AlignRow, ConfirmButton, TableData } from '@framework/ui';
import { message, Space, TableColumnsType, Tooltip } from 'antd';
import React, { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { ISubmissionQuote } from './ISubmissionQuote';
import { quoteColumns } from './quoteColumns';
import { QuoteDownloadButton } from './QuoteDownloadButton';
import { SubmissionQuoteLogic } from './SubmissionQuoteLogic';

interface IQuoteTableProps {
  readonly?: boolean;
  quotes?: ISubmissionQuote[];
  submissionId: string | null | undefined;
}

export function QuoteTable(props: IQuoteTableProps) {
  const { readonly = false, quotes, submissionId } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const accept = usePutFrontRichiesteIdPreventiviIdAccettazione();

  const acceptQuote = useCallback(
    async (record: PreventivoDTO, toAccept: boolean) => {
      try {
        await accept.mutateAsync({
          id: submissionId ?? '<unknown>',
          data: { accetta: toAccept },
          idPreventivo: record.id ?? '<unknown>',
        });
        await queryClient.invalidateQueries(getGetFrontRichiesteIdPreventiviQueryKey(submissionId ?? '<unknown>')); // prettier-ignore
        await queryClient.invalidateQueries(getGetFrontRichiesteIdQueryKey(submissionId ?? '<unknown>')); // prettier-ignore
        await queryClient.invalidateQueries(getGetFrontRichiesteIdPagamentiQueryKey(submissionId ?? '<unknown>')); // prettier-ignore
      } catch (e) {
        console.error(`[QuoteTable] Impossibile accettare il preventivo`, { e }); // prettier-ignore
        message.error(getNetworkErrorMessage(e, t("quote-table.errore-in-accettazione-preventivo", 'Si è verificato un problema durante l\'accettazione del preventivo'))); // prettier-ignore
      }
    },
    [accept, queryClient, submissionId],
  );

  if (!quotes) {
    return null;
  }

  return (
    <TableData
      size="small"
      pagination={false}
      dataSource={quotes}
      expandable={
        readonly
          ? undefined
          : {
              showExpandColumn: quotes.some(q =>
                SubmissionQuoteLogic.requiresAcceptance(q),
              ),
              rowExpandable: record =>
                SubmissionQuoteLogic.requiresAcceptance(record),
              // expandRowByClick: true,
              defaultExpandAllRows: true,
              expandedRowRender: record =>
                !SubmissionQuoteLogic.requiresAcceptance(record) ? undefined : (
                  <div>
                    <AlignRow justify="flex-end">
                      <Space>
                        <ConfirmButton
                          size="large"
                          buttonDanger
                          onConfirm={async () =>
                            await acceptQuote(record, false)
                          }
                          loading={accept.isLoading}
                          title={t(
                            'quote-table.azioni.button-confirm.refuse.message.rifiuto-preventivo',
                            'Sei sicuro di voler rifiutare il Preventivo?',
                          )}
                        >
                          {t(
                            'quote-table.azioni.button-confirm.refuse.text',
                            'RIFIUTA',
                          )}
                        </ConfirmButton>
                        <ConfirmButton
                          size="large"
                          popconfirmButtonDanger={false}
                          onConfirm={async () =>
                            await acceptQuote(record, true)
                          }
                          loading={accept.isLoading}
                          title={
                            <Space direction="vertical" size={0}>
                              <div>
                                {t(
                                  'quote-table.azioni.button-confirm.accept.message.accetta-preventivo-prima-riga',
                                  'Confermi di aver scaricato e letto il il preventivo nella sua interezza.',
                                )}
                              </div>
                              <div>
                                {t(
                                  'quote-table.azioni.button-confirm.accept.message.accetta-preventivo-seconda-riga',
                                  'Confermi di voler accettare il preventivo?',
                                )}
                              </div>
                            </Space>
                          }
                        >
                          {t(
                            'quote-table.azioni.button-confirm.accept.text',
                            'ACCETTA',
                          )}
                        </ConfirmButton>
                      </Space>
                    </AlignRow>
                  </div>
                ),
            }
      }
      columns={
        [
          ...quoteColumns,
          {
            title: t('quote-table.azioni', 'Azioni'),
            key: 'actions',
            align: 'right',
            hidden: readonly,
            render: (_, record) => (
              <Space>
                <Tooltip
                  title={t(
                    'quote-table.azioni.button.tooltips.scarica-preventivo',
                    'Scarica Preventivo',
                  )}
                >
                  <QuoteDownloadButton
                    submissionId={submissionId ?? '<unknown>'}
                    quoteId={record.id ?? '<unknown>'}
                  />
                </Tooltip>
              </Space>
            ),
          },
        ] satisfies TableColumnsType<ISubmissionQuote>
      }
    />
  );
}
